import type { AnswerQuestionMatching, AnswerQuestionMatchingAnswer, ExportAnswerMatching, FeedbackAnswerQuestionMatching, QuestionMatchingData } from '@repo/types/questions/QuestionMatching';

import { QuestionMatching } from '@repo/lib/quiz/questions/class/QuestionMatching';
import { shuffleArray } from '@repo/lib/shuffleArray';
import { useMemo, useState } from 'react';

export function useQuestionMarkedMatching(question: QuestionMatchingData, marked: FeedbackAnswerQuestionMatching | null, answer: ExportAnswerMatching | undefined) {
  const questionObj = useMemo<QuestionMatching>(() => {
    const qClass = new QuestionMatching(question, null, marked);

    if (answer) qClass.importAnswer(answer);

    return qClass;
  }, [question, marked, answer]);

  const score: number = questionObj.getPoints();
  const totalPoints: number = questionObj.getMaxPoints();
  const correct: boolean = marked?.answers?.every((answer) => answer.correct) ?? false;
  const isMarked: boolean = questionObj.isMarked();
  const correctState = questionObj.correctState();

  const getByColumns = (answers: AnswerQuestionMatching[]) => {
    const left: AnswerQuestionMatchingAnswer[] = [];
    const right: AnswerQuestionMatchingAnswer[] = [];

    answers.forEach((pair) => {
      left.push(pair.left);
      right.push(pair.right);
    });

    return [shuffleArray(left), shuffleArray(right)];
  };

  const [columns] = useState<AnswerQuestionMatchingAnswer[][]>(getByColumns(question.answers));

  const getMarkedState = (answer: AnswerQuestionMatchingAnswer) => {
    const markedAnswerLeft = marked?.answers?.find((pair) => pair.match?.left === answer.id);
    const markedAnswerRight = marked?.answers?.find((pair) => pair.match?.right === answer.id);
    if (markedAnswerLeft) {
      return markedAnswerLeft.correct;
    }
    if (markedAnswerRight) {
      return markedAnswerRight.correct;
    }
    return undefined;
  };

  return {
    questionObj,
    isMarked,
    columns,
    getMarkedState,
    score,
    totalPoints,
    correct,
    correctState,
  };
}
